import styled from 'styled-components';
import Button from '../../../components/Atoms/Button';
import { tokens, media } from '../../../design/tokens';

export const Container = styled.div`
  width: 100%;
  max-width: 100%;

  /*
   * Sem margin-top. Havia 68px aqui, resíduo de quando o header era fixed —
   * nesta rota o HeaderV2 é estático (position: inherit, entra no fluxo e
   * já ocupa seus 88px), então a margem era espaço morto empilhado sobre um
   * header que não estava sobreposto a nada. Era a causa principal da tela
   * precisar de scroll: o conteúdo cabia, a margem é que não.
   */
  padding: ${tokens.space.xl} ${tokens.space.gutter} 40px;

  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.upTo(tokens.breakpoint.sm)} {
    padding: ${tokens.space.lg} ${tokens.space.gutter};
  }
`;

export const PageTitle = styled.h1`
  width: 100%;
  max-width: 640px;

  font-family: ${tokens.font.title.family};
  font-size: ${tokens.font.title.size};
  font-weight: ${tokens.font.title.weight};
  color: ${tokens.color.textWhite};
  letter-spacing: -0.02em;
  margin: 0 0 ${tokens.space.lg};

  ${media.upTo(tokens.breakpoint.sm)} {
    max-width: 480px;
  }
`;

/*
 * O painel é o que faltava para a tela parar de ler como "elementos soltos
 * boiando no preto": um degrau tonal acima do palco (mesma lógica do
 * Grafite de Card em DESIGN.md), não uma cor nova, dá contenção ao
 * formulário inteiro. cardCharcoal sobre stageBlack é um salto pequeno
 * (#222 sobre #0f0f0f) — a sombra de "assentamento de imagem", já
 * documentada em DESIGN.md para separar capa escura de fundo escuro, dá o
 * relevo que só o tom não entrega.
 */
export const Panel = styled.div`
  width: 100%;
  max-width: 640px;

  background: ${tokens.color.cardCharcoal};
  border-radius: ${tokens.radius.md};
  padding: ${tokens.space.xl};
  box-shadow: ${tokens.shadow.imageSeat};

  /*
   * Duas colunas no desktop: identidade à esquerda, formulário à direita.
   * Empilhado, o conteúdo pedia ~922px de altura numa janela de 788 e a tela
   * exigia scroll — apertar só espaçamento não recuperava os 134px que
   * faltavam sem deixar tudo abafado. Lado a lado, a altura passa a ser a do
   * formulário sozinho, e o espaço horizontal (que estava vazio) faz o
   * trabalho. Abaixo de 768px volta a empilhar, onde rolar é natural.
   */
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${tokens.space.xl};

  ${media.upTo(tokens.breakpoint.sm)} {
    flex-direction: column;
    align-items: center;
    gap: 0;
    max-width: 480px;
    padding: ${tokens.space.lg};
  }
`;

/* Coluna da identidade. Largura casada com os 150px que o ImageUpload
   declara para o próprio contêiner, para o avatar e o botão "Editar"
   compartilharem o mesmo eixo. */
export const Identity = styled.div`
  flex-shrink: 0;
  width: 150px;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ImageContainer = styled.div`
  position: relative;
  flex-shrink: 0;

  margin-bottom: ${tokens.space.sm};

  .main-profile-img {
    width: 84px;
    height: 84px;
    border-radius: ${tokens.radius.circle};
    border: solid 2px ${tokens.color.fieldSlate};
  }

  .child-profile-img {
    position: absolute;
    width: 30px;
    height: 30px;
    background: ${tokens.color.stageBlack};
    border-radius: ${tokens.radius.circle};
    border: solid 2px ${tokens.color.fieldSlate};

    bottom: -6px;
    right: -6px;
  }
`;

export const MainImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: ${tokens.radius.circle};
  object-fit: cover;
  flex-shrink: 0;
`;

export const ChildImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: ${tokens.radius.circle};
  object-fit: cover;
  flex-shrink: 0;
`;

/* Só faz sentido empilhado: no desktop quem separa identidade de formulário
   é o gap entre as colunas, não uma régua horizontal. */
export const Divider = styled.hr`
  display: none;

  ${media.upTo(tokens.breakpoint.sm)} {
    display: block;
    width: 100%;
    border: 0;
    border-top: 1px solid ${tokens.color.fieldSlate};
    margin: ${tokens.space.lg} 0 ${tokens.space.xl};
  }
`;

export const FormFields = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: ${tokens.space.lg};

  /* Mesma regra de Login/Password/SignUp: o gap acima decide o ritmo, não a
     margin própria do Input/Button, senão as duas somam. */
  && > div,
  && > button {
    margin-top: 0;
  }
`;

/*
 * Um campo por linha, todos na largura cheia da coluna.
 *
 * Uma versão anterior pareava "Apelido" e "Sala" lado a lado a ~48%, copiando
 * o padrão de endereço do SignUp. Não serve aqui: os valores desta tela são
 * longos (nome completo repetido no apelido, "1 - ESCOLAIMPULSE" na sala) e
 * eram truncados nos dois campos ao mesmo tempo. Campo estreito só vale
 * quando o dado é curto por natureza (CEP, UF, número) — não é o caso.
 *
 * Os Input recebem customWidth="100%" no index: o átomo tem 75% como padrão,
 * herdado de telas de outra geometria, e sem isso os campos não preenchiam a
 * coluna nem batiam com a largura do botão — a borda direita do formulário
 * ficava irregular.
 */
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xs};
`;

/*
 * Rótulo visível de verdade, sempre presente — diferente do placeholder do
 * Input, que só aparece com o campo vazio. Nesta tela todo campo já nasce
 * preenchido (defaultValue), então um rótulo só de placeholder nunca chega
 * a aparecer: nenhum campo tinha nome legível. Este <span> não é um
 * <label htmlFor>: o Input já registra seu próprio rótulo acessível (oculto,
 * visualmente) a partir do `placeholder` — dois <label> apontando pro mesmo
 * campo têm leitura inconsistente entre leitores de tela. Aqui é só o texto
 * visível para quem enxerga a tela; o nome acessível continua vindo do
 * `placeholder`, com o mesmo texto, para os dois nunca divergirem.
 */
export const FieldLabel = styled.span`
  font-family: ${tokens.font.caption.family};
  font-size: ${tokens.font.caption.size};
  font-weight: 600;
  color: ${tokens.color.textAsh};
`;

export const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
